import { formatToOneDecimal, getCurrentAbsenceBaseYear } from '@/services/utils';
import type { AbsencePercentage, AbsenceValues } from '@/types/app';

export type AbsenceInfo = {
	rates_by_year?: Record<number, string | number>;
	absence_percentage_list?: AbsencePercentage[];
} | null;

const SECTOR_AVERAGE_SOURCE = 'sector_info';

/**
 * Derive the editable absence rates from the committed absence_info.
 *
 * The vz1..vz3 order is what fixes the years on screen: vz1 is last year, vz3 is
 * three years back — most recent first. Shared so every surface showing absence
 * rates lists them in that same order.
 */
export const toAbsenceValues = (absenceInfo: AbsenceInfo): AbsenceValues => {
	if (!absenceInfo) return { vz1: '', vz2: '', vz3: '' };
	const currentYear = getCurrentAbsenceBaseYear();
	return {
		vz1: formatToOneDecimal(absenceInfo.rates_by_year?.[currentYear - 1]),
		vz2: formatToOneDecimal(absenceInfo.rates_by_year?.[currentYear - 2]),
		vz3: formatToOneDecimal(absenceInfo.rates_by_year?.[currentYear - 3])
	};
};

/**
 * Years pre-filled with the sector average while other years hold real figures (e.g. a
 * partner's initial_payload that skips a year). Only a mix is flagged: when every year is
 * the sector average the summary card warning already says the whole block needs input.
 */
export const getSectorAverageYears = (absenceInfo: AbsenceInfo): Set<string> => {
	const list = absenceInfo?.absence_percentage_list ?? [];
	const sectorYears = list
		.filter(({ source }) => source === SECTOR_AVERAGE_SOURCE)
		.map(({ year }) => year);
	return sectorYears.length < list.length ? new Set(sectorYears) : new Set();
};
